
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaChevronDown,
  FaBars,
  FaTimes,
  FaLinkedin,
  FaInstagram,
  FaFacebook,
  FaTwitter,
} from 'react-icons/fa';
import logo from './pngeis.png';
import './Navbar.css';
import { FaYoutube } from 'react-icons/fa';

// ---------------------------------------------------------------------------
// Static navigation data — identical labels/routes to the original component.
// Pulled out of the component body so they aren't recreated on every render.
// ---------------------------------------------------------------------------
const WHAT_WE_DO = {
  'IT Engineering Solutions': [
    { name: 'AI/ML Model development', path: '/aimlmodel' },
    { name: 'NLP Solutions', path: '/nlp' },
    { name: 'Data Engineering and Analytics', path: '/dataengineering' },
    { name: 'Web Development', path: '/webdev' },
    { name: 'ERP Platform Solutions', path: '/erp' },
    { name: 'Custom AI Solutions', path: '/customai' },
  ],
  'Mech Engineering Solutions': [
    { name: 'Engineering Design Solutions', path: '/engneeringdesignsolution' },
    { name: 'Industrial Automation', path: '/industrialautomate' },
    { name: 'Digital Factory', path: '/digitalfactory' },
  ],
  'Digital Marketing & Creative Solutions': [
    { name: 'Digital Marketing Solution', path: '/dmspage' },
    { name: 'Graphics Design Solution', path: '/gdspage' },
    { name: 'Multimedia Solutions', path: '/ms' },
  ],
};

const INDUSTRIES = {
  'Financial Services': [{ name: 'Banking & Capital Markets', path: '/banking' }],
  'Healthcare & Life Sciences': [{ name: 'Healthcare & Life Sciences', path: '/healthsci' }],
  Technology: [
    { name: 'High Tech', path: '/hitech' },
    { name: 'Software & Digital Platforms', path: '/software' },
  ],
  'Automotive & Mobility': [{ name: 'Automotive', path: '/automotive' }],
};

const PRODUCTS = [
  { name: 'Learning Management System', path: '/lmspage' },
  { name: 'Digital Menu & POS', path: '/billingsys' },
  { name: 'HRMS (Human Resource Management)', path: '/hrmspage' },
];

const SOCIAL_LINKS = [
  { Icon: FaLinkedin, url: 'https://www.linkedin.com/company/envision-integrated-services-pvt-ltd/posts/?feedView=all', label: 'LinkedIn' },
  // { Icon: FaInstagram, url: 'https://instagram.com', label: 'Instagram' },
  // { Icon: FaFacebook, url: 'https://facebook.com', label: 'Facebook' },
  { Icon: FaTwitter, url: 'https://x.com/Envision_is', label: 'Twitter' },
  { Icon: FaYoutube, url: 'https://www.youtube.com/@EnvisionISGroup', label: 'Youtube' },
];

// Maps every submenu path back to its parent dropdown key, so we can tell
// from the current URL alone whether "What We Do", "Products" or "Industry"
// should render as active — even when the active page is a submenu page.
const buildParentLookup = () => {
  const lookup = {};
  Object.values(WHAT_WE_DO).forEach((items) =>
    items.forEach(({ path }) => {
      lookup[path] = 'what-we-do';
    })
  );
  Object.values(INDUSTRIES).forEach((items) =>
    items.forEach(({ path }) => {
      lookup[path] = 'industry';
    })
  );
  PRODUCTS.forEach(({ path }) => {
    lookup[path] = 'products';
  });
  return lookup;
};
const PARENT_LOOKUP = buildParentLookup();

/**
 * Enterprise-grade primary navigation.
 *
 * Dropdown behaviour (click-driven, not hover):
 *  - Clicking a parent item opens its dropdown.
 *  - Clicking the same parent again closes it.
 *  - Clicking a different parent swaps the open dropdown.
 *  - Clicking outside, pressing Escape, choosing a submenu item, or
 *    navigating to a new route all close the dropdown.
 *  - Only one dropdown is ever open at a time.
 */
const Navbar = () => {
  const location = useLocation();

  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'what-we-do' | 'products' | 'industry' | null
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openMobileSection, setOpenMobileSection] = useState(null);

  const dropdownNavRef = useRef(null);
  const sidebarRef = useRef(null);
  const toggleButtonRef = useRef(null);

  // Which top-level menu (if any) corresponds to the current URL.
  const activeParent = useMemo(
    () => PARENT_LOOKUP[location.pathname] || null,
    [location.pathname]
  );

  // ---- Scrolled navbar look -------------------------------------------------
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll(); // set correct state on mount (e.g. deep-linked, already scrolled)
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ---- Close every open menu whenever the route changes --------------------
  useEffect(() => {
    setActiveDropdown(null);
    setIsSidebarOpen(false);
    setOpenMobileSection(null);
  }, [location.pathname]);

  // ---- Click outside closes the open desktop dropdown -----------------------
  useEffect(() => {
    if (!activeDropdown) return undefined;
    const handleClickOutside = (event) => {
      if (dropdownNavRef.current && !dropdownNavRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // ---- Click outside closes the mobile sidebar -------------------------------
  useEffect(() => {
    if (!isSidebarOpen) return undefined;
    const handleClickOutside = (event) => {
      const clickedTrigger = toggleButtonRef.current?.contains(event.target);
      const clickedInsideSidebar = sidebarRef.current?.contains(event.target);
      if (!clickedTrigger && !clickedInsideSidebar) {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarOpen]);

  // ---- Escape closes whatever is open ----------------------------------------
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setIsSidebarOpen(false);
      setActiveDropdown(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ---- Lock body scroll while the mobile sidebar is open ---------------------
  useEffect(() => {
    document.body.style.overflow = isSidebarOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  // ---- Handlers ---------------------------------------------------------------
  const toggleDropdown = useCallback((name) => {
    setActiveDropdown((prev) => (prev === name ? null : name));
  }, []);

  const closeDropdown = useCallback(() => setActiveDropdown(null), []);

  const toggleSidebar = useCallback(() => setIsSidebarOpen((prev) => !prev), []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
    setOpenMobileSection(null);
  }, []);

  const toggleMobileSection = useCallback((name) => {
    setOpenMobileSection((prev) => (prev === name ? null : name));
  }, []);

  // ---- Contact button "eyes" micro-interaction (kept from the original) -------
  const eyesRef = useRef(null);
  const [eyeOffset, setEyeOffset] = useState({ x: 0, y: 0 });
  const [isContactHovered, setIsContactHovered] = useState(false);

  const trackEyes = useCallback((event) => {
    if (!eyesRef.current) return;
    const rect = eyesRef.current.getBoundingClientRect();
    const dx = event.clientX - (rect.left + rect.width / 2);
    const dy = event.clientY - (rect.top + rect.height / 2);
    setEyeOffset({
      x: Math.max(-6, Math.min(6, dx / 20)),
      y: Math.max(-6, Math.min(6, dy / 20)),
    });
  }, []);

  const headerClassName = `nav-header${isScrolled ? ' scrolled' : ''}`;

  return (
    <>
      <header className={headerClassName}>
        <nav className="navbar" aria-label="Primary">
          <div className="nav-container">
            {/* ---------------------------------------------------------- */}
            {/* Logo                                                       */}
            {/* ---------------------------------------------------------- */}
            <div className="logo-container">
              <Link className="nav-logo" to="/" onClick={closeSidebar} aria-label="Envision home">
                <img src={logo} alt="" className="logo-image" />
                <div className="logo-text">
                  <h1 className="logo-text-main">Envision</h1>
                  <span className="logo-text-sub">Integrated Services Pvt. Ltd.</span>
                </div>
              </Link>
            </div>

            {/* ---------------------------------------------------------- */}
            {/* Desktop menu                                               */}
            {/* ---------------------------------------------------------- */}
            <div className="nav-menu">
              <ul className="nav-list" ref={dropdownNavRef}>
                <li className="nav-item">
                  <Link
                    to="/who-we-are"
                    className={`nav-link${location.pathname === '/who-we-are' ? ' active' : ''}`}
                  >
                    <span className="nav-text">Who We Are</span>
                  </Link>
                </li>

                {/* What We Do -------------------------------------------- */}
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link${activeDropdown === 'what-we-do' || activeParent === 'what-we-do' ? ' active' : ''}`}
                    aria-haspopup="true"
                    aria-expanded={activeDropdown === 'what-we-do'}
                    onClick={() => toggleDropdown('what-we-do')}
                  >
                    <span className="nav-text">What We Do</span>
                    <FaChevronDown
                      aria-hidden="true"
                      className={`dropdown-icon${activeDropdown === 'what-we-do' ? ' rotated' : ''}`}
                    />
                  </button>

                  {activeDropdown === 'what-we-do' && (
                    <div className="mega-dropdown what-we-do-dropdown" role="menu">
                      <div className="dropdown-content">
                        <h2 className="dropdown-title">
                          <span className="title-accent">Our</span> Services
                        </h2>
                        <p className="dropdown-subtitle">
                          Comprehensive solutions for your business needs
                        </p>

                        <div className="what-we-do-grid">
                          {Object.entries(WHAT_WE_DO).map(([category, items]) => (
                            <div key={category} className="what-we-do-column">
                              <h3 className="what-we-do-category">
                                <span className="category-icon" aria-hidden="true">▸</span>
                                {category}
                              </h3>
                              <ul className="what-we-do-list">
                                {items.map((item) => (
                                  <li key={item.path}>
                                    <Link
                                      to={item.path}
                                      className={`what-we-do-link${location.pathname === item.path ? ' active' : ''}`}
                                      role="menuitem"
                                      onClick={closeDropdown}
                                    >
                                      <span className="link-text">{item.name}</span>
                                      <span className="link-arrow" aria-hidden="true">→</span>
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </li>

                {/* Products ------------------------------------------------ */}
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link${activeDropdown === 'products' || activeParent === 'products' ? ' active' : ''}`}
                    aria-haspopup="true"
                    aria-expanded={activeDropdown === 'products'}
                    onClick={() => toggleDropdown('products')}
                  >
                    <span className="nav-text">Products</span>
                    <FaChevronDown
                      aria-hidden="true"
                      className={`dropdown-icon${activeDropdown === 'products' ? ' rotated' : ''}`}
                    />
                  </button>

                  {activeDropdown === 'products' && (
                    <div className="mega-dropdown products-dropdown" role="menu">
                      <div className="dropdown-content">
                        <h2 className="dropdown-title">
                          <span className="title-accent">Our</span> Products
                        </h2>
                        <p className="dropdown-subtitle">
                          Innovative products for your business needs
                        </p>

                        <ul className="products-list">
                          {PRODUCTS.map((product) => (
                            <li key={product.path} className="product-item">
                              <Link
                                to={product.path}
                                className={`product-link${location.pathname === product.path ? ' active' : ''}`}
                                role="menuitem"
                                onClick={closeDropdown}
                              >
                                <span className="product-name">{product.name}</span>
                                <span className="product-arrow" aria-hidden="true">→</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </li>

                {/* Industry ------------------------------------------------- */}
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link${activeDropdown === 'industry' || activeParent === 'industry' ? ' active' : ''}`}
                    aria-haspopup="true"
                    aria-expanded={activeDropdown === 'industry'}
                    onClick={() => toggleDropdown('industry')}
                  >
                    <span className="nav-text">Industry</span>
                    <FaChevronDown
                      aria-hidden="true"
                      className={`dropdown-icon${activeDropdown === 'industry' ? ' rotated' : ''}`}
                    />
                  </button>

                  {activeDropdown === 'industry' && (
                    <div className="mega-dropdown industry-dropdown" role="menu">
                      <div className="dropdown-content">
                        <h2 className="dropdown-title">
                          <span className="title-accent">Industry</span> Solutions
                        </h2>
                        <p className="dropdown-subtitle">
                          Industry-specific solutions and expertise
                        </p>

                        <ul className="products-list">
                          {Object.entries(INDUSTRIES).map(([industry, items]) => (
                            <li key={industry} className="product-item">
                              <Link
                                to={items[0].path}
                                className={`product-link${location.pathname === items[0].path ? ' active' : ''}`}
                                role="menuitem"
                                onClick={closeDropdown}
                              >
                                <span className="product-name">{industry}</span>
                                <span className="product-arrow" aria-hidden="true">→</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </li>

                <li className="nav-item">
                  <Link
                    to="/careerpage"
                    className={`nav-link${location.pathname === '/careerpage' ? ' active' : ''}`}
                  >
                    <span className="nav-text">Careers</span>
                  </Link>
                </li>

                <li className="nav-item social-icons">
                  <div className="social-icons-container">
                    {SOCIAL_LINKS.map(({ Icon, url, label }) => (
                      <a
                        key={label}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon-link"
                        aria-label={label}
                      >
                        <Icon aria-hidden="true" />
                      </a>
                    ))}
                  </div>
                </li>

                <li className="nav-item contact-item">
                  <Link
                    to="/contactus"
                    className="nav-contact-button"
                    style={{
                      background: "#0f7a99",
                      color: "#fff",
                      borderRadius: "50px",
                      padding: "12px 24px",
                      height: "48px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textDecoration: "none",
                      fontSize: "15px",
                      fontWeight: "600",
                      transition: "0.3s ease",
                    }}
                  >
                    Let's Connect
                  </Link>
                </li>
              </ul>
            </div>

            {/* ---------------------------------------------------------- */}
            {/* Mobile toggle                                              */}
            {/* ---------------------------------------------------------- */}
            <button
              ref={toggleButtonRef}
              type="button"
              className="mobile-toggle"
              onClick={toggleSidebar}
              aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isSidebarOpen}
              aria-controls="mobile-sidebar"
            >
              {isSidebarOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
            </button>
          </div>
        </nav>
      </header>

      {/* -------------------------------------------------------------- */}
      {/* Mobile sidebar                                                  */}
      {/* -------------------------------------------------------------- */}
      <div
        id="mobile-sidebar"
        className={`sidebar${isSidebarOpen ? ' open' : ''}`}
        ref={sidebarRef}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        aria-hidden={!isSidebarOpen}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo-container">
            <img src={logo} alt="" className="sidebar-logo" />
            <span className="sidebar-logo-text-main">Envision</span>
          </div>
          <button
            type="button"
            className="sidebar-close"
            onClick={closeSidebar}
            aria-label="Close menu"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        <div className="sidebar-content">
          <div className="sidebar-section">
            <Link to="/who-we-are" className="sidebar-link" onClick={closeSidebar}>
              <span className="sidebar-link-text">Who We Are</span>
            </Link>
          </div>

          <div className="sidebar-section">
            <button
              type="button"
              className="sidebar-accordion-trigger"
              aria-expanded={openMobileSection === 'what-we-do'}
              onClick={() => toggleMobileSection('what-we-do')}
            >
              <span className="sidebar-title">What We Do</span>
              <FaChevronDown
                aria-hidden="true"
                className={`dropdown-icon${openMobileSection === 'what-we-do' ? ' rotated' : ''}`}
              />
            </button>
            {openMobileSection === 'what-we-do' && (
              <div className="sidebar-accordion-panel">
                {Object.entries(WHAT_WE_DO).map(([category, items]) => (
                  <div key={category} className="sidebar-category-section">
                    <p className="sidebar-category-title">{category}</p>
                    <ul className="sidebar-list">
                      {items.map((item) => (
                        <li key={item.path}>
                          <Link to={item.path} className="sidebar-link" onClick={closeSidebar}>
                            <span className="sidebar-link-text">{item.name}</span>
                            <span className="sidebar-link-arrow" aria-hidden="true">→</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sidebar-section">
            <button
              type="button"
              className="sidebar-accordion-trigger"
              aria-expanded={openMobileSection === 'products'}
              onClick={() => toggleMobileSection('products')}
            >
              <span className="sidebar-title">Products</span>
              <FaChevronDown
                aria-hidden="true"
                className={`dropdown-icon${openMobileSection === 'products' ? ' rotated' : ''}`}
              />
            </button>
            {openMobileSection === 'products' && (
              <div className="sidebar-accordion-panel">
                <ul className="sidebar-list">
                  {PRODUCTS.map((product) => (
                    <li key={product.path}>
                      <Link to={product.path} className="sidebar-link" onClick={closeSidebar}>
                        <span className="sidebar-link-text">{product.name}</span>
                        <span className="sidebar-link-arrow" aria-hidden="true">→</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="sidebar-section">
            <button
              type="button"
              className="sidebar-accordion-trigger"
              aria-expanded={openMobileSection === 'industry'}
              onClick={() => toggleMobileSection('industry')}
            >
              <span className="sidebar-title">Industry</span>
              <FaChevronDown
                aria-hidden="true"
                className={`dropdown-icon${openMobileSection === 'industry' ? ' rotated' : ''}`}
              />
            </button>
            {openMobileSection === 'industry' && (
              <div className="sidebar-accordion-panel">
                {Object.entries(INDUSTRIES).map(([industry, items]) => (
                  <Link
                    key={industry}
                    to={items[0].path}
                    className="sidebar-industry-link"
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-category-title">{industry}</span>
                    <span className="sidebar-industry-sub">
                      {items.map((item) => item.name).join(', ')}
                    </span>
                    <span className="sidebar-link-arrow" aria-hidden="true">→</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="sidebar-section">
            <Link to="/careerpage" className="sidebar-link" onClick={closeSidebar}>
              <span className="sidebar-link-text">Careers</span>
            </Link>
          </div>

          <div className="sidebar-section">
            <p className="sidebar-title">Follow Us</p>
            <div className="sidebar-social-icons">
              {SOCIAL_LINKS.map(({ Icon, url, label }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sidebar-social-icon"
                  aria-label={label}
                >
                  <Icon aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          <div className="sidebar-actions">
            <Link to="/contactus" className="sidebar-button" onClick={closeSidebar}>
              <span>Contact Us</span>
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </div>

      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar} aria-hidden="true" />
      )}
    </>
  );
};

export default Navbar;